import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MfaSetup from "@/pages/MfaSetup";
import Dashboard from "@/pages/Dashboard";
import ResourcePage from "@/components/ResourcePage";
import Users from "@/pages/Users";
import FeeGrids from "@/pages/finance/FeeGrids";
import InvoicesList from "@/pages/finance/InvoicesList";
import MonthlyBatch from "@/pages/finance/MonthlyBatch";
import FinanceDashboard from "@/pages/finance/FinanceDashboard";
import CashAccounts from "@/pages/finance/CashAccounts";
import Expenses from "@/pages/finance/Expenses";
import PaymentsList from "@/pages/finance/PaymentsList";
import ReceiptsList from "@/pages/finance/ReceiptsList";
import FamilyAccount from "@/pages/finance/FamilyAccount";
import ReportCards from "@/pages/ReportCards";
import Messages from "@/pages/Messages";
import Appointments from "@/pages/Appointments";
import Documents from "@/pages/Documents";
import Settings from "@/pages/Settings";
import SchoolCalendar from "@/pages/SchoolCalendar";
import AdmissionRequirements from "@/pages/admin/AdmissionRequirements";
import DocumentsRegistry from "@/pages/documents/DocumentsRegistry";
import PdfTemplates from "@/pages/documents/PdfTemplates";
import DocumentLogs from "@/pages/documents/DocumentLogs";
import Profile from "@/pages/Profile";
import SchoolDashboard from "@/pages/school/SchoolDashboard";
import SchoolLevels from "@/pages/school/Levels";
import SchoolGroups from "@/pages/school/Groups";
import TeacherAssignments from "@/pages/school/TeacherAssignments";
import SchoolYears from "@/pages/school/SchoolYears";
import SchoolRooms from "@/pages/school/Rooms";
import SchoolPeriods from "@/pages/school/Periods";
import Timetable from "@/pages/school/Timetable";
import SchoolAudit from "@/pages/school/Audit";
import StudentsDirectory from "@/pages/people/StudentsDirectory";
import StudentProfile from "@/pages/people/StudentProfile";
import FamiliesDirectory from "@/pages/people/FamiliesDirectory";
import FamilyProfile from "@/pages/people/FamilyProfile";
import ParentsDirectory from "@/pages/people/ParentsDirectory";
import ParentProfile from "@/pages/people/ParentProfile";
import TeachersDirectory from "@/pages/people/TeachersDirectory";
import TeacherProfile from "@/pages/people/TeacherProfile";
import CardSheet from "@/pages/people/CardSheet";
import CardsDirectory from "@/pages/people/CardsDirectory";
import AdmissionsDirectory from "@/pages/admin/AdmissionsDirectory";
import AdmissionDetail from "@/pages/admin/AdmissionDetail";
import ScanCard from "@/pages/people/ScanCard";
import SecurityAdmin from "@/pages/SecurityAdmin";
import PedagogySessions from "@/pages/pedagogy/Sessions";
import PedagogyHomework from "@/pages/pedagogy/Homework";
import PedagogyEvaluations from "@/pages/pedagogy/Evaluations";
import PedagogySupervision from "@/pages/pedagogy/Supervision";
import { Loader2 } from "lucide-react";

function Protected({ children, allowPending }) {
  const { user, checked } = useAuth();
  if (!checked) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mfa_pending && !allowPending) return <Navigate to="/mfa-setup" replace />;
  return children;
}

function StaffRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("direction", "administration")) return <Navigate to="/dashboard" replace />;
  return children;
}

function SchoolRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction", "administration", "coordinateur", "professeur"))
    return <Navigate to="/dashboard" replace />;
  return children;
}

function AuditRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction")) return <Navigate to="/dashboard" replace />;
  return children;
}

function SecurityRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction")) return <Navigate to="/dashboard" replace />;
  return children;
}

function PedagogyRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction", "administration", "coordinateur", "professeur"))
    return <Navigate to="/dashboard" replace />;
  return children;
}

function PedagogySupRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction", "administration", "coordinateur"))
    return <Navigate to="/dashboard" replace />;
  return children;
}

function FamilyRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction", "administration", "coordinateur", "parent"))    return <Navigate to="/dashboard" replace />;
  return children;
}

function PeopleStaffRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction", "administration", "coordinateur"))
    return <Navigate to="/dashboard" replace />;
  return children;
}

function AdmissionRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction", "administration", "coordinateur", "finance"))
    return <Navigate to="/dashboard" replace />;
  return children;
}

function FinanceRoute({ children }) {
  const { hasRole } = useAuth();
  if (!hasRole("super_admin", "direction", "administration", "finance"))
    return <Navigate to="/dashboard" replace />;
  return children;
}

const R = (key) => <ResourcePage resourceKey={key} />;

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/mfa-setup" element={<Protected allowPending><MfaSetup /></Protected>} />
              <Route path="/" element={<Protected><Layout /></Protected>}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="profile" element={<Profile />} />
                <Route path="school" element={<SchoolRoute><SchoolDashboard /></SchoolRoute>} />
                <Route path="school/levels" element={<SchoolRoute><SchoolLevels /></SchoolRoute>} />
                <Route path="school/groups" element={<SchoolRoute><SchoolGroups /></SchoolRoute>} />
                <Route path="school/teacher-assignments" element={<SchoolRoute><TeacherAssignments /></SchoolRoute>} />
                <Route path="school/rooms" element={<SchoolRoute><SchoolRooms /></SchoolRoute>} />
                <Route path="school/periods" element={<SchoolRoute><SchoolPeriods /></SchoolRoute>} />
                <Route path="school/timetable" element={<SchoolRoute><Timetable /></SchoolRoute>} />
                <Route path="students" element={<Navigate to="/people/students" replace />} />
                <Route path="families" element={<Navigate to="/people/families" replace />} />
                <Route path="parents" element={<Navigate to="/people/parents" replace />} />
                <Route path="teachers" element={<Navigate to="/people/teachers" replace />} />
                <Route path="school-years" element={<SchoolYears />} />
                <Route path="levels" element={<Navigate to="/school/levels" replace />} />
                <Route path="groups" element={<Navigate to="/school/groups" replace />} />
                <Route path="subjects" element={R("subjects")} />
                <Route path="enrollments" element={<Navigate to="/admin/admissions" replace />} />
                <Route path="homework" element={<Navigate to="/pedagogy/homework" replace />} />
                <Route path="grades" element={<Navigate to="/pedagogy/grades" replace />} />
                <Route path="attendance" element={<Navigate to="/pedagogy/attendance" replace />} />
                <Route path="pedagogy/my-sessions" element={<PedagogyRoute><PedagogySessions focus="sessions" /></PedagogyRoute>} />
                <Route path="pedagogy/cahier" element={<PedagogyRoute><PedagogySessions focus="cahier" /></PedagogyRoute>} />
                <Route path="pedagogy/attendance" element={<PedagogyRoute><PedagogySessions focus="attendance" /></PedagogyRoute>} />
                <Route path="pedagogy/homework" element={<PedagogyRoute><PedagogyHomework /></PedagogyRoute>} />
                <Route path="pedagogy/grades" element={<PedagogyRoute><PedagogyEvaluations /></PedagogyRoute>} />
                <Route path="pedagogy/supervision" element={<PedagogySupRoute><PedagogySupervision /></PedagogySupRoute>} />
                <Route path="calendar" element={<SchoolCalendar />} />
                <Route path="admin/school-calendar" element={<SchoolCalendar />} />
                <Route path="admin/requirements" element={<AdmissionRequirements />} />
                <Route path="admin/security" element={<SecurityRoute><SecurityAdmin /></SecurityRoute>} />
                <Route path="documents" element={<DocumentsRegistry />} />
                <Route path="documents/templates" element={<PdfTemplates />} />
                <Route path="documents/logs" element={<DocumentLogs />} />
                <Route path="report-cards" element={<ReportCards />} />
                <Route path="invoices" element={<Navigate to="/finance/invoices" replace />} />
                <Route path="payments" element={<Navigate to="/finance/payments" replace />} />
                <Route path="receipts" element={<Navigate to="/finance/receipts" replace />} />
                <Route path="finance/fee-grids" element={<FinanceRoute><FeeGrids /></FinanceRoute>} />
                <Route path="finance/invoices" element={<FinanceRoute><InvoicesList /></FinanceRoute>} />
                <Route path="finance/monthly-batch" element={<FinanceRoute><MonthlyBatch /></FinanceRoute>} />
                <Route path="finance/payments" element={<FinanceRoute><PaymentsList /></FinanceRoute>} />
                <Route path="finance/receipts" element={<FinanceRoute><ReceiptsList /></FinanceRoute>} />
                <Route path="finance/accounts" element={<FinanceRoute><FamilyAccount /></FinanceRoute>} />
                <Route path="finance/dashboard" element={<FinanceRoute><FinanceDashboard /></FinanceRoute>} />
                <Route path="finance/cash-accounts" element={<FinanceRoute><CashAccounts /></FinanceRoute>} />
                <Route path="finance/expenses" element={<FinanceRoute><Expenses /></FinanceRoute>} />
                <Route path="documents" element={<Documents />} />
                <Route path="messages" element={<Messages />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="action-plans" element={R("action-plans")} />
                <Route path="sensitive-cases" element={R("sensitive-cases")} />
                <Route path="users" element={<Users />} />
                <Route path="settings" element={<StaffRoute><Settings /></StaffRoute>} />
                <Route path="audit" element={<AuditRoute><SchoolAudit /></AuditRoute>} />
                <Route path="people/students" element={<SchoolRoute><StudentsDirectory /></SchoolRoute>} />
                <Route path="people/students/:id" element={<StudentProfile />} />
                <Route path="people/families" element={<FamilyRoute><FamiliesDirectory /></FamilyRoute>} />
                <Route path="people/families/:id" element={<FamilyRoute><FamilyProfile /></FamilyRoute>} />
                <Route path="people/parents" element={<PeopleStaffRoute><ParentsDirectory /></PeopleStaffRoute>} />
                <Route path="people/parents/:id" element={<PeopleStaffRoute><ParentProfile /></PeopleStaffRoute>} />
                <Route path="people/teachers" element={<PeopleStaffRoute><TeachersDirectory /></PeopleStaffRoute>} />
                <Route path="people/teachers/:id" element={<TeacherProfile />} />
                <Route path="people/cards" element={<PeopleStaffRoute><CardsDirectory /></PeopleStaffRoute>} />
                <Route path="people/cards/print" element={<PeopleStaffRoute><CardSheet /></PeopleStaffRoute>} />
                <Route path="people/scan" element={<AuditRoute><ScanCard /></AuditRoute>} />
                <Route path="admin/admissions" element={<AdmissionRoute><AdmissionsDirectory /></AdmissionRoute>} />
                <Route path="admin/admissions/:id" element={<AdmissionRoute><AdmissionDetail /></AdmissionRoute>} />
                <Route path="audit-logs" element={<Navigate to="/audit" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
